import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { SettingsProvider } from './context/SettingsContext';
import { ScrollToTop } from './components/ScrollToTop';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SEOHead } from './components/SEOHead';
import { detectLangFromPath, localizePath } from './utils/i18nPath';
import { Content } from './components/Content';
import { PriceList } from './components/prices/PriceList';
import { Gallery } from './components/gallery/Gallery';
import { Team } from './components/team/Team';

// Public pages are imported statically ON PURPOSE.
//
// They are pre-rendered by react-snap and hydrated with `hydrateRoot`. Putting
// them behind React.lazy would mean the client's first render hits a Suspense
// fallback while the chunk downloads, which can make React discard the
// pre-rendered markup it was supposed to hydrate. They are only ~40 kB raw in
// total — the real bundle win came from isolating Firebase, not from splitting
// these.

// The admin panel is one lazy boundary that also owns AuthProvider — this is
// what keeps firebase/auth, Cloud Storage and the full Firestore SDK out of
// the public bundle. See components/admin/AdminApp.js.
const AdminApp = lazy(() => import('./components/admin/AdminApp').then(m => ({ default: m.AdminApp })));

const AdminFallback = () => (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
);

// 404 Not Found page
const NotFound = () => {
    const lang = typeof window !== 'undefined'
        ? detectLangFromPath(window.location.pathname)
        : 'uk';
    const homeHref = localizePath('/', lang);
    return (
        <div className="min-h-screen flex flex-col items-center justify-center text-center px-4">
            <h1 className="text-6xl font-bold text-gold mb-4">404</h1>
            <SEOHead
                title="404 — UDent"
                description="Сторінку не знайдено / Page not found"
                noindex={true}
            />
            <p className="text-xl text-gray-600 mb-8">Сторінку не знайдено / Page not found</p>
            <a href={homeHref} className="px-6 py-3 bg-gold text-white font-semibold rounded-full hover:bg-gold-dark transition-colors">
                Головна / Home
            </a>
        </div>
    );
};

function App() {
    return (
        <div>
            <SettingsProvider>
                <Router>
                    <ErrorBoundary>
                        <ScrollToTop />
                        <Routes>
                            {/* Public — Ukrainian (default) */}
                            <Route path='/' element={<Content />} />
                            <Route path='/prices' element={<PriceList />} />
                            <Route path='/gallery' element={<Gallery />} />
                            <Route path='/team' element={<Team />} />

                            {/* Public — English (separate canonical URLs for SEO) */}
                            <Route path='/en' element={<Content />} />
                            <Route path='/en/prices' element={<PriceList />} />
                            <Route path='/en/gallery' element={<Gallery />} />
                            <Route path='/en/team' element={<Team />} />

                            {/* Admin — lazy chunk, owns its AuthProvider.
                                Suspense stays scoped to this route: react-snap
                                writes no <!--$--> markers, so a boundary around
                                <Routes> broke hydration on every public page. */}
                            <Route
                                path='/admin/*'
                                element={
                                    <Suspense fallback={<AdminFallback />}>
                                        <AdminApp />
                                    </Suspense>
                                }
                            />

                            {/* 404 — catch all unmatched routes */}
                            <Route path='*' element={<NotFound />} />
                        </Routes>
                    </ErrorBoundary>
                </Router>
            </SettingsProvider>
        </div>
    );
}

export default App;
